import { useBlocker } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../ui/alert-dialog";
import { useRoom } from "../../lib/rooms";
import { activeCall, useActiveCall } from "../../room/active-call";
import type { ShouldBlockFn } from "@tanstack/react-router";

// Asks before entering another room while a call is live. Every dismissal
// stays; only the confirm action leaves.
export function RoomSwitchDialog({
  open,
  roomName,
  onStay,
  onLeave,
}: {
  open: boolean;
  roomName: string;
  onStay: () => void;
  onLeave: () => void;
}) {
  const { t } = useTranslation();
  return (
    <AlertDialog
      open={open}
      onOpenChange={(next) => {
        if (!next) onStay();
      }}
    >
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{t("callBar.switchTitle")}</AlertDialogTitle>
          <AlertDialogDescription className="wrap-anywhere">
            {roomName
              ? t("callBar.switchBody", { name: roomName })
              : t("callBar.switchBodyUnnamed")}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>{t("callBar.stay")}</AlertDialogCancel>
          <AlertDialogAction
            variant="destructive"
            onClick={(event) => {
              // The blocker closes the dialog; closing here would also report a stay.
              event.preventDefault();
              onLeave();
            }}
          >
            {t("callBar.switchConfirm")}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

// Read at navigation time, so the blocker never re-registers on call changes.
const entersAnotherRoom: ShouldBlockFn = ({ next }) => {
  const live = activeCall.liveRoomId();
  return Boolean(live) && next.routeId === "/room/$roomId" && next.params.roomId !== live;
};

// One router-level guard covers links, programmatic navigation and browser
// back/forward. A typed URL reloads the document, which has no call to guard.
export function RoomSwitchGuard() {
  const blocker = useBlocker({
    shouldBlockFn: entersAnotherRoom,
    enableBeforeUnload: false,
    withResolver: true,
  });
  const roomId = useActiveCall()?.roomId;
  if (blocker.status !== "blocked" || !roomId) return null;
  return (
    <LiveRoomSwitchDialog
      roomId={roomId}
      onStay={blocker.reset}
      onLeave={() => {
        // End the live call first so the next room starts clean, even behind a passcode.
        activeCall.endCall();
        blocker.proceed();
      }}
    />
  );
}

function LiveRoomSwitchDialog({
  roomId,
  onStay,
  onLeave,
}: {
  roomId: string;
  onStay: () => void;
  onLeave: () => void;
}) {
  // The live room was entered in this document, so its query is usually cached.
  const { data: room } = useRoom(roomId);
  return <RoomSwitchDialog open roomName={room?.name ?? ""} onStay={onStay} onLeave={onLeave} />;
}
